export { BAC_LOCALITY_BAND_METERS, bacLocalityParams, distanceMeters, geoAnchor, localityBand, mapAnchor } from "./host-activity-geo";

/**
 * Only explicitly non-US relevance searches may use the isolated BAC
 * catalogue. Shared DS/US, global and price-replica requests stay unchanged.
 */
export function bacActivityIndexFor(request: Record<string, unknown>, primary: string, isolated?: string): string | null {
  if (!isolated || isolated === primary || request.indexName !== primary) return null;
  const params = request.params && typeof request.params === "object" ? request.params as Record<string, unknown> : request;
  const filters = JSON.stringify([params.filters, params.facetFilters, params.numericFilters]);
  if (/platform_origin[^A-Z]*DRIVESHARE/i.test(filters) || /country_code[^A-Z]*US\b/i.test(filters)) return null;
  const countries = [...filters.matchAll(/country_code:([A-Z]{2})\b/g)].map(m => m[1]);
  return countries.length && countries.every(c => c !== "US") ? isolated : null;
}
// Shared Algolia contract for BookAclassic.
//
// This module is the single source of truth for the cross-platform pieces
// of the car-search Algolia integration: the default index name, the
// sort-replica naming scheme, the refinement attribute names, and the
// hit -> card mapping. It is consumed by the web hooks
// (artifacts/bookaclassic), the mobile hooks (artifacts/mobile) and the
// server-side index sync (artifacts/api-server).
//
// IMPORTANT: the Algolia *index settings* (attributesForFaceting,
// searchableAttributes, custom ranking, etc.) deliberately live in
// `artifacts/api-server/.../algolia-faceting.mjs` so the plain-Node
// reindex script and the Inngest writers share one faceting source. A
// cross-snapshot test pins those writers to that `.mjs` module, so do NOT
// migrate faceting settings here.

// --- Index name -----------------------------------------------------------

/**
 * Client-side fallback index name. The web (`VITE_ALGOLIA_INDEX_NAME`) and
 * mobile (`EXPO_PUBLIC_ALGOLIA_INDEX_NAME`) bundles fall back to this when
 * the env var is unset. The server intentionally has NO hard-coded default
 * (see `resolveAlgoliaIndexName`) and must not use this constant.
 */
export const DEFAULT_ALGOLIA_INDEX_NAME = "cars";

// --- Sort replicas --------------------------------------------------------

/**
 * Logical sort keys exposed in the UI. `relevance` maps to the base index;
 * every other key maps to a dedicated Algolia replica index.
 */
export type AlgoliaSortKey =
  | "relevance"
  | "price_asc"
  | "price_desc"
  | "newest"
  | "top_rated";

/**
 * Suffix appended to the base index name to form each replica index, e.g.
 * `cars` -> `cars_price_asc`. The server provisions replicas with exactly
 * these names; the clients point `useSortBy` at them.
 */
export const ALGOLIA_SORT_REPLICA_SUFFIXES: Record<
  Exclude<AlgoliaSortKey, "relevance">,
  string
> = {
  price_asc: "_price_asc",
  price_desc: "_price_desc",
  newest: "_newest",
  top_rated: "_top_rated",
};

/**
 * Resolve the Algolia index name for a given sort key. `relevance` returns
 * the base index unchanged; all other keys append the replica suffix.
 */
export function sortIndexName(baseIndex: string, key: AlgoliaSortKey): string {
  if (key === "relevance") return baseIndex;
  return `${baseIndex}${ALGOLIA_SORT_REPLICA_SUFFIXES[key]}`;
}

/**
 * The ordered sort options shown in the search UI. Labels are shared so the
 * web and mobile dropdowns stay in lockstep.
 */
export const ALGOLIA_SORT_OPTIONS: { key: AlgoliaSortKey; label: string }[] = [
  { key: "relevance", label: "Relevance" },
  { key: "price_asc", label: "Price: Low to High" },
  { key: "price_desc", label: "Price: High to Low" },
  { key: "newest", label: "Newest Year" },
  { key: "top_rated", label: "Top Rated" },
];

// --- Refinement attributes ------------------------------------------------

/**
 * Algolia document field / refinement attribute names used by the search
 * widgets. Centralising these keeps web and mobile pointed at the same
 * facets and avoids the class of bug where one platform refines a
 * never-indexed attribute (e.g. the legacy `air_con`, fixed to
 * `has_aircon` on web — see task #1867).
 */
export const ALGOLIA_ATTRS = {
  rentalDriving: "rental_driving_active",
  rentalChauffeured: "rental_chauffeured_active",
  rentalEvent: "rental_event_active",
  make: "make",
  countryCode: "country_code",
  eras: "eras",
  carTypes: "car_types",
  colourId: "colour_id",
  instantBook: "instant_book",
  convertible: "convertible",
  automatic: "automatic",
  hasAircon: "has_aircon",
  priceDriving: "price_driving",
  priceEvent: "price_event",
  chauffeuredMinHourly: "chauffeured_min_price_hourly_rate",
  filterPricePerDay: "filter_price_per_day",
} as const;

// --- Hit -> card mapping --------------------------------------------------

/** Raw Algolia hit shape — a record of arbitrary indexed fields. */
export type AlgoliaCarHit = Record<string, unknown>;

/**
 * Normalised car-card shape produced from an Algolia hit. Structurally
 * matches each artifact's local `CarCardData` so the mapping output can be
 * consumed directly by web and mobile result hooks.
 */
export interface AlgoliaCarCard {
  id: number;
  slug: string;
  platformOrigin?: string | null;
  legacySlug?: string | null;
  make: string;
  model: string;
  year: number;
  city: string;
  state: string;
  priceDriving: number | null;
  priceEvent: number | null;
  chauffeurMinPrice: number | null;
  eventMinPrice: number | null;
  eventHourlyRate: number | null;
  chauffeuredHourlyRate: number | null;
  minimumPrice: number | null;
  primaryImageUrl: string | null;
  primaryImageCdnThumbnail?: string | null;
  primaryImageCdnMedium?: string | null;
  primaryImageCdnFull?: string | null;
  // Full image set for the in-card carousel (primary first). Optional —
  // older index records (pre-`images` reindex) only carry the primary
  // image, in which case the card renders a single, non-swipe slide.
  images?: Array<{
    url: string | null;
    cdnThumbnail?: string | null;
    cdnMedium?: string | null;
    cdnFull?: string | null;
  }>;
  rentalDrivingActive: boolean;
  rentalChauffeurActive: boolean;
  rentalEventActive: boolean;
  totalScore: number | null;
  reviewsRating: number | null;
  reviewsCount: number | null;
  transmission: string | null;
  passengers: number | null;
  currencyCode: string;
  latitude: number | null;
  longitude: number | null;
  countryCode?: string | null;
  ownerProTier?: string | null;
  instantBook?: boolean;
  /** Curated, admin-set `cars.featured` flag — drives the "Featured" card badge. */
  featured?: boolean;
}

/**
 * Map a raw Algolia hit to the normalised car-card shape. Shared verbatim
 * by the web and mobile search-result hooks so the two platforms render
 * identical card data from the same index.
 */
export function mapAlgoliaHitToCarCard(hit: AlgoliaCarHit): AlgoliaCarCard {
  const geo = hit._geoloc as { lat: number; lng: number } | undefined;
  const platformOrigin =
    typeof hit.platform_origin === "string" ? hit.platform_origin : null;
  const isDriveShareOrigin = ["ds", "driveshare"].includes(
    String(platformOrigin || "").trim().toLowerCase(),
  );
  const legacySlug =
    hit.country_code === "US"
    && isDriveShareOrigin
    && typeof hit.old_slug === "string"
    && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(hit.old_slug)
      ? hit.old_slug
      : null;
  return {
    id:
      typeof hit.objectID === "string"
        ? parseInt(hit.objectID as string, 10) || 0
        : (hit.objectID as number),
    slug: (hit.slug as string) || "",
    platformOrigin,
    legacySlug,
    make: (hit.make as string) || "",
    model: (hit.model as string) || "",
    year: (hit.year as number) || 0,
    city: (hit.city as string) || "",
    state: (hit.state as string) || "",
    priceDriving: (hit.price_driving as number | null) ?? null,
    priceEvent: (hit.price_event as number | null) ?? null,
    chauffeurMinPrice:
      (hit.chauffeured_min_price_hourly_rate as number | null) ?? null,
    eventMinPrice: (hit.event_min_price_hourly_rate as number | null) ?? null,
    eventHourlyRate: (hit.event_hourly_rate as number | null) ?? null,
    chauffeuredHourlyRate: (hit.chauffeured_hourly_rate as number | null) ?? null,
    minimumPrice: (hit.minimum_price as number | null) ?? null,
    primaryImageUrl:
      (hit.image as string | null) ??
      (hit.image_medium as string | null) ??
      (hit.image_thumbnail as string | null) ??
      (hit.image_full as string | null) ??
      null,
    primaryImageCdnThumbnail: (hit.image_thumbnail as string | null) ?? null,
    primaryImageCdnMedium: (hit.image_medium as string | null) ?? null,
    primaryImageCdnFull: (hit.image_full as string | null) ?? null,
    images: Array.isArray(hit.images)
      ? (hit.images as Array<Record<string, unknown>>).map((img) => ({
          url: (img.url as string | null) ?? null,
          cdnThumbnail: (img.cdnThumbnail as string | null) ?? null,
          cdnMedium: (img.cdnMedium as string | null) ?? null,
          cdnFull: (img.cdnFull as string | null) ?? null,
        }))
      : undefined,
    rentalDrivingActive: (hit.rental_driving_active as boolean) || false,
    rentalChauffeurActive: (hit.rental_chauffeured_active as boolean) || false,
    rentalEventActive: (hit.rental_event_active as boolean) || false,
    totalScore: (hit.total_score as number | null) ?? null,
    reviewsRating:
      hit.reviews_rating != null ? (hit.reviews_rating as number) : null,
    reviewsCount: (hit.reviews_count as number | null) ?? null,
    transmission: hit.automatic ? "Automatic" : null,
    passengers: (hit.passengers as number | null) ?? null,
    currencyCode: (hit.currency_code as string) || "USD",
    // Existing index records may still have three-decimal salted fuzz. Round
    // those at consumption too; never jitter an already-fuzzed index point.
    latitude: typeof geo?.lat === "number" && Number.isFinite(geo.lat) ? Number(geo.lat.toFixed(2)) : null,
    longitude: typeof geo?.lng === "number" && Number.isFinite(geo.lng) ? Number(geo.lng.toFixed(2)) : null,
    ownerProTier: (hit.owner_pro_tier as string | null) ?? null,
    instantBook: (hit.instant_book as boolean) || false,
    countryCode: (hit.country_code as string | null) ?? null,
    featured: (hit.featured as boolean) || false,
  };
}
